import React from 'react';
import type { AnyNetworkElement } from '../../../../../types/network';
import type { LoadLevelThresholds } from '../../../../../services/settingsService';
import type {
  BusbarSwitchModel,
  StationCableField,
  StationCouplingField,
  StationOutletField,
  StationSection,
  ValueSource,
} from '../../../../../utils/stationDiagramModel';
import { fmt } from '../../../../../utils/numberFormat';
import { loadingTextClass } from '../../../../../utils/loadingStyles';

/**
 * Übersichtsschaltplan of a transformer station or a Kabelverteilerschrank as
 * one SVG — the style of a 0,4-kV-Anlagenplan (MS-Einspeisung → Trafo →
 * Messung → Hauptschalter → Sammelschiene → NH-Abgänge → Ziel; a KVS has no
 * spine, one busbar section per Sammelschiene and couplers between them),
 * drawn from the live model instead of a CAD drawing. Pure renderer:
 * everything it shows comes in through props.
 *
 * Colours are Tailwind `text-*` classes on `currentColor` fills/strokes so
 * the drawing follows the theme like every other component; the Ampel of a
 * fuse or cable follows the instance's cable levels (fuses judged on the
 * cable levels, see CableRow).
 */

export interface StationSpine {
  designator: string;
  name: string;
  /** HV/LV level, e.g. { hvKv: 20, lvKv: 0.4 }. */
  hvKv: number;
  lvKv: number;
  ratingKva: number | null;
  ratedCurrentA: number | null;
  nameplate: string[];
  loadingPct: number | null;
  activePowerKw: number | null;
  reactivePowerKvar: number | null;
  apparentPowerKva: number | null;
  inService: boolean;
  voltageV: (number | null)[];
  voltageSource: ValueSource | null;
  currentA: (number | null)[];
  currentSource: ValueSource | null;
  frequencyHz: number | null;
  cosPhi: number | null;
  cosPhiSource: ValueSource | null;
  measuredAtLabel: string | null;
  calcAtLabel: string | null;
}

interface Props {
  /** Transformer spine above the busbar — absent for a KVS. */
  spine?: StationSpine | null;
  sections: StationSection[];
  /** Couplers between KVS busbar sections. */
  switches?: BusbarSwitchModel[];
  cableLevels: LoadLevelThresholds;
  trafoLevels: LoadLevelThresholds;
  /** Scale the drawing to the container width instead of 1:1. */
  fitWidth: boolean;
  ariaLabel: string;
  onSelectElement: (element: AnyNetworkElement) => void;
  onSelectCoupling: (couplingId: string) => void;
}

// ── Geometry ────────────────────────────────────────────────────────────
export const STATION_COL_W = 172;
const PAD_X = 56;
const SPINE_BUSBAR_Y = 296;
const PLAIN_BUSBAR_Y = 76;
const FIELD_H = 292;
const SECTION_GAP = 100;

// ── Theme classes (currentColor) ────────────────────────────────────────
const C = {
  line: 'text-gray-700 dark:text-primary-200',
  lineSoft: 'text-gray-400 dark:text-primary-400',
  busbar: 'text-gray-900 dark:text-white',
  text: 'text-gray-900 dark:text-white',
  muted: 'text-gray-500 dark:text-primary-300',
  faint: 'text-gray-400 dark:text-primary-400',
  measured: 'text-teal-600 dark:text-teal-300',
  calculated: 'text-blue-600 dark:text-blue-300',
  closed: 'text-emerald-600 dark:text-emerald-400',
  open: 'text-orange-600 dark:text-orange-400',
  noData: 'text-gray-300 dark:text-primary-600',
  hover: 'fill-transparent group-hover:fill-gray-200 dark:group-hover:fill-primary-600',
  box: 'fill-gray-50 dark:fill-primary-700 stroke-gray-300 dark:stroke-primary-500',
  boxHover: 'group-hover:stroke-accent-primary',
};

const DEST_STYLE: Record<string, { glyph: string; label: string; cls: string }> = {
  kabelverteilschrank: { glyph: 'KV', label: 'Kabelverteiler', cls: 'text-cyan-700 dark:text-cyan-300' },
  hausanschluss: { glyph: '⌂', label: 'Hausanschluss', cls: 'text-green-700 dark:text-green-300' },
  muffe: { glyph: '●', label: 'Muffe', cls: 'text-purple-700 dark:text-purple-300' },
  trafostation: { glyph: 'T', label: 'Trafostation', cls: 'text-orange-700 dark:text-orange-300' },
  zone: { glyph: '⇄', label: 'Nachbarzone', cls: 'text-emerald-700 dark:text-emerald-300' },
  open: { glyph: '?', label: 'Ziel', cls: 'text-gray-500 dark:text-primary-300' },
  none: { glyph: '–', label: 'Reserve', cls: 'text-gray-400 dark:text-primary-400' },
};

const SWITCH_TYPE_LABEL: Record<string, string> = {
  fuse: 'NH-Sicherung',
  switch: 'Lasttrennschalter',
  lbs: 'Lasttrennschalter',
  breaker: 'Leistungsschalter',
  ls: 'Leistungsschalter',
  disconnector: 'Trenner',
};

const switchTypeLabel = (t: string | null | undefined, fallback: string) =>
  SWITCH_TYPE_LABEL[(t ?? '').toLowerCase()] ?? (t || fallback);

/** Fill class for a loading figure on the instance's Ampel (fuse body, cable line). */
function loadingColor(pct: number | null, t: LoadLevelThresholds): string {
  if (pct == null || !Number.isFinite(pct)) return C.noData;
  if (pct >= t.red) return 'text-red-500';
  if (pct >= t.orange) return 'text-orange-500';
  if (pct >= t.yellow) return 'text-yellow-400';
  if (pct >= t.green) return 'text-green-500';
  return 'text-gray-400 dark:text-primary-400';
}

const cut = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);
const phases = (v: (number | null)[], digits = 0) => v.map(x => (x == null ? '—' : fmt(x, digits))).join(' / ');

interface TextProps {
  x: number;
  y: number;
  anchor?: 'start' | 'middle' | 'end';
  size?: number;
  bold?: boolean;
  mono?: boolean;
  className?: string;
  title?: string;
  children: React.ReactNode;
}

const T: React.FC<TextProps> = ({ x, y, anchor = 'start', size = 10, bold, mono, className = C.text, title, children }) => (
  <text
    x={x}
    y={y}
    textAnchor={anchor}
    fontSize={size}
    fontWeight={bold ? 600 : 400}
    fontFamily={mono ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : undefined}
    fill="currentColor"
    className={`${className} tabular-nums select-none`}
  >
    {title && <title>{title}</title>}
    {children}
  </text>
);

const SourceDot: React.FC<{ x: number; y: number; source: ValueSource | null }> = ({ x, y, source }) => (
  source ? (
    <circle cx={x} cy={y} r={2.4} fill="currentColor" className={source === 'measured' ? C.measured : C.calculated}>
      <title>{source === 'measured' ? 'gemessen' : 'berechnet (Lastfluss)'}</title>
    </circle>
  ) : null
);

/** NH fuse (DIN EN 60617): a box on the conductor; a pulled fuse hangs out of the holder. */
const FuseSymbol: React.FC<{ x: number; y: number; colorClass: string; inserted: boolean; dashed?: boolean }> = ({ x, y, colorClass, inserted, dashed }) => {
  const h = 30;
  const w = 14;
  if (!inserted) {
    return (
      <g>
        <line x1={x} y1={y - 8} x2={x} y2={y} stroke="currentColor" strokeWidth={1.5} className={C.line} />
        <g transform={`rotate(-38 ${x} ${y})`}>
          <rect x={x - w / 2} y={y} width={w} height={h} rx={2} fill="none" stroke="currentColor" strokeWidth={1.5} strokeDasharray="3 2" className={C.faint} />
          <line x1={x} y1={y} x2={x} y2={y + h} stroke="currentColor" strokeWidth={1} className={C.faint} />
        </g>
        <line x1={x} y1={y + h} x2={x} y2={y + h + 10} stroke="currentColor" strokeWidth={1.5} className={C.line} />
      </g>
    );
  }
  return (
    <g>
      <line x1={x} y1={y - 8} x2={x} y2={y + h + 10} stroke="currentColor" strokeWidth={1.5} className={C.line} />
      <rect
        x={x - w / 2}
        y={y}
        width={w}
        height={h}
        rx={2}
        fill="currentColor"
        stroke="currentColor"
        strokeWidth={1.2}
        strokeDasharray={dashed ? '3 2' : undefined}
        className={colorClass}
      />
      <rect x={x - w / 2} y={y} width={w} height={h} rx={2} fill="none" stroke="currentColor" strokeWidth={1} className={C.line} opacity={0.55} />
    </g>
  );
};

/** Vertical disconnector / switch: straight contact when closed, swung out when open. */
const SwitchSymbol: React.FC<{ x: number; y: number; closed: boolean; colorClass: string }> = ({ x, y, closed, colorClass }) => (
  <g className={colorClass}>
    <circle cx={x} cy={y} r={2.2} fill="currentColor" />
    <circle cx={x} cy={y + 30} r={2.2} fill="currentColor" />
    {closed
      ? <line x1={x} y1={y} x2={x} y2={y + 30} stroke="currentColor" strokeWidth={2} />
      : <line x1={x} y1={y + 30} x2={x + 13} y2={y + 4} stroke="currentColor" strokeWidth={2} />}
  </g>
);

/** Horizontal busbar coupler between x1 and x2 on the line y. */
const CouplerSymbol: React.FC<{ x1: number; x2: number; y: number; closed: boolean; colorClass: string }> = ({ x1, x2, y, closed, colorClass }) => (
  <g className={colorClass}>
    <circle cx={x1} cy={y} r={2.4} fill="currentColor" />
    <circle cx={x2} cy={y} r={2.4} fill="currentColor" />
    {closed
      ? <line x1={x1} y1={y} x2={x2} y2={y} stroke="currentColor" strokeWidth={2.2} />
      : <line x1={x1} y1={y} x2={x2 - 6} y2={y - 14} stroke="currentColor" strokeWidth={2.2} />}
  </g>
);

// ── Field columns ───────────────────────────────────────────────────────

interface ColumnProps {
  index: number;
  x0: number;
  y0: number;
}

const FieldTag: React.FC<{ xl: number; y0: number; index: number; kind: 'N' | 'F' | 'W'; yOff: number }> = ({ xl, y0, index, kind, yOff }) => (
  <T x={xl - 8} y={y0 + yOff} anchor="end" size={8.5} mono className={C.faint}>
    {kind === 'N' ? '+' : '-'}{kind}{String(index + 1).padStart(2, '0')}
  </T>
);

const DestinationBox: React.FC<{
  x0: number;
  y: number;
  kind: string;
  name: string;
  detail?: string | null;
  dashed?: boolean;
  onClick?: () => void;
  title?: string;
}> = ({ x0, y, kind, name, detail, dashed, onClick, title }) => {
  const style = DEST_STYLE[kind] ?? DEST_STYLE.open;
  const w = STATION_COL_W - 14;
  return (
    <g onClick={onClick} className={onClick ? 'cursor-pointer' : undefined}>
      {title && <title>{title}</title>}
      <rect x={x0 + 7} y={y} width={w} height={detail ? 54 : 42} rx={6} strokeWidth={1} strokeDasharray={dashed ? '4 3' : undefined} className={`${C.box} ${onClick ? C.boxHover : ''}`} />
      <circle cx={x0 + 23} cy={y + 21} r={10} fill="currentColor" className={style.cls} opacity={0.15} />
      <T x={x0 + 23} y={y + 24.5} anchor="middle" size={kind === 'kabelverteilschrank' ? 8 : 11} bold className={style.cls}>{style.glyph}</T>
      <T x={x0 + 39} y={y + 16} size={8.5} className={C.muted}>{style.label}</T>
      <T x={x0 + 39} y={y + 29} size={10.5} bold title={name}>{cut(name, 18)}</T>
      {detail && <T x={x0 + 39} y={y + 44} size={8.5} className={C.muted}>{cut(detail, 22)}</T>}
    </g>
  );
};

const CableColumn: React.FC<ColumnProps & {
  field: StationCableField;
  cableLevels: LoadLevelThresholds;
  onSelectElement: (el: AnyNetworkElement) => void;
}> = ({ field: f, index, x0, y0, cableLevels, onSelectElement }) => {
  const xl = x0 + 40;
  const xt = xl + 13;
  const fuseColor = f.fuse
    ? (f.fuse.isInserted ? loadingColor(f.fuseLoadingPct, cableLevels) : C.faint)
    : C.noData;
  const cableColor = f.inService ? loadingColor(f.loadingPct, cableLevels) : C.faint;
  const cableWidth = f.crossSectionMm2 == null ? 2 : f.crossSectionMm2 >= 150 ? 3.5 : f.crossSectionMm2 >= 95 ? 3 : f.crossSectionMm2 >= 50 ? 2.5 : 2;
  const fuseLabel = f.fuse ? `${f.fuse.type} · ${fmt(f.fuse.current, 0)} A` : 'ohne Angabe';
  const fuseState = !f.fuse
    ? null
    : !f.fuse.isInserted
      ? 'gezogen'
      : f.fuseLoadingPct == null ? '—' : `${fmt(f.fuseLoadingPct, 0)} % Sich.`;
  const fuseStateClass = !f.fuse || !f.fuse.isInserted || f.fuseLoadingPct == null
    ? C.muted
    : loadingTextClass(f.fuseLoadingPct, cableLevels);
  const loadingLabel = f.loadingPct == null ? '—' : `${fmt(f.loadingPct, 0)} %`;
  const loadingClass = f.loadingPct == null ? C.muted : loadingTextClass(f.loadingPct, cableLevels);
  const showStrangMax = f.strangMaxPct != null && f.loadingPct != null && f.strangMaxPct > f.loadingPct + 1;
  const powerLabel = f.powerKw == null
    ? '—'
    : `${fmt(Math.abs(f.powerKw), 1)} kW${f.feedsBusbar ? ' ↑' : ''}`;
  const extraLine = !f.inService
    ? { text: 'außer Betrieb', cls: C.open }
    : f.feedsBusbar || showStrangMax
      ? {
        text: [f.feedsBusbar ? 'speist ein' : null, showStrangMax ? `Strang max. ${fmt(f.strangMaxPct!, 0)} %` : null].filter(Boolean).join(' · '),
        cls: f.feedsBusbar ? C.closed : C.muted,
      }
      : null;
  const dest = f.destination;
  const destDetail = [
    dest.viaMuffen > 0 ? `über ${dest.viaMuffen} Muffe${dest.viaMuffen > 1 ? 'n' : ''}` : null,
    dest.houseCount > 0 ? `${dest.houseCount} HA` : null,
    f.lengthM ? `${fmt(f.lengthM, 0)} m` : null,
  ].filter(Boolean).join(' · ');
  const meta = [
    f.crossSectionMm2 ? `${fmt(f.crossSectionMm2, 0)} mm²` : null,
    f.slot != null ? `Slot ${f.slot}` : null,
  ].filter(Boolean).join(' · ');

  return (
    <g className="group">
      <rect x={x0 + 2} y={y0 + 6} width={STATION_COL_W - 4} height={FIELD_H - 8} rx={8} className={C.hover} fillOpacity={0.5} />
      {/* stub from the busbar */}
      <line x1={xl} y1={y0} x2={xl} y2={y0 + 30} stroke="currentColor" strokeWidth={1.5} className={C.line} />
      <FieldTag xl={xl} y0={y0} index={index} kind="N" yOff={17} />

      {/* fuse */}
      <g onClick={() => onSelectElement(f.cable)} className="cursor-pointer">
        <title>{`${fuseLabel}${f.fuse ? (f.fuse.isInserted ? '' : ' (gezogen)') : ''}${f.fuseSource === 'outlet' ? ' · Nennstrom laut Messgerät' : ''} — Kabel auf der Karte auswählen`}</title>
        <FuseSymbol x={xl} y={y0 + 36} colorClass={fuseColor} inserted={f.fuse?.isInserted ?? true} dashed={f.fuseSource === 'outlet'} />
      </g>
      <FieldTag xl={xl} y0={y0} index={index} kind="F" yOff={54} />
      <T x={xt} y={y0 + 48} size={10.5} bold>{fuseLabel}</T>
      {fuseState && <T x={xt} y={y0 + 61} size={9.5} bold={f.fuseLoadingPct != null} className={fuseStateClass}>{fuseState}</T>}

      {/* cable */}
      <g onClick={() => onSelectElement(f.cable)} className="cursor-pointer">
        <title>{`${f.cable.name} — Kabel auf der Karte auswählen`}</title>
        <line
          x1={xl}
          y1={y0 + 76}
          x2={xl}
          y2={y0 + 186}
          stroke="currentColor"
          strokeWidth={cableWidth}
          strokeDasharray={f.inService ? undefined : '5 4'}
          className={cableColor}
        />
        <line x1={xl} y1={y0 + 76} x2={xl} y2={y0 + 186} stroke="transparent" strokeWidth={14} />
      </g>
      {/* flow arrow: down = draws from the busbar, up = feeds it */}
      {f.feedsBusbar
        ? <polygon points={`${xl - 4},${y0 + 88} ${xl + 4},${y0 + 88} ${xl},${y0 + 80}`} fill="currentColor" className={C.closed} />
        : <polygon points={`${xl - 4},${y0 + 184} ${xl + 4},${y0 + 184} ${xl},${y0 + 192}`} fill="currentColor" className={C.line} />}
      <FieldTag xl={xl} y0={y0} index={index} kind="W" yOff={100} />
      <T x={xt} y={y0 + 95} size={10.5} bold title={f.cableLabel}>{cut(f.cableLabel, 17)}</T>
      {meta && <T x={xt} y={y0 + 108} size={9} className={C.muted}>{meta}</T>}

      <SourceDot x={xt + 3} y={y0 + 127} source={f.currentSource} />
      <T x={xt + 9} y={y0 + 130} size={9.5} title="Strom L1 / L2 / L3 an der Sammelschiene">
        {f.currentSource ? `${phases(f.phaseCurrentsA)} A` : (f.currentA != null ? `${fmt(f.currentA, 0)} A` : 'I —')}
      </T>
      <T x={xt + 9} y={y0 + 144} size={9.5} title={f.feedsBusbar ? 'Wirkleistung in die Sammelschiene (Einspeisung)' : 'Wirkleistung aus der Sammelschiene'}>
        P {powerLabel}
      </T>
      <T x={xt + 9} y={y0 + 160} size={11} bold className={loadingClass} title="Auslastung des Kabels an der Sammelschiene">
        {loadingLabel}
      </T>
      {extraLine && (
        <T x={xt + 9} y={y0 + 172} size={8.5} className={extraLine.cls} title={showStrangMax ? 'Höchste Auslastung eines Abschnitts dieses Kabels' : undefined}>
          {extraLine.text}
        </T>
      )}

      <DestinationBox
        x0={x0}
        y={y0 + 200}
        kind={dest.kind}
        name={dest.label}
        detail={destDetail || null}
        onClick={dest.element ? () => onSelectElement(dest.element!) : undefined}
        title={dest.element ? `${dest.label} — auf der Karte anzeigen` : undefined}
      />
    </g>
  );
};

const CouplingColumn: React.FC<ColumnProps & {
  field: StationCouplingField;
  cableLevels: LoadLevelThresholds;
  onSelectCoupling: (id: string) => void;
}> = ({ field: f, index, x0, y0, cableLevels, onSelectCoupling }) => {
  const xl = x0 + 40;
  const xt = xl + 13;
  const stateClass = f.isClosed ? C.closed : C.open;
  const flow = f.flow;
  const loading = flow?.loadingPercent ?? null;
  const cableColor = f.isClosed ? loadingColor(loading, cableLevels) : C.faint;
  const powerKw = flow?.powerKw ?? null;
  return (
    <g className="group">
      <rect x={x0 + 2} y={y0 + 6} width={STATION_COL_W - 4} height={FIELD_H - 8} rx={8} className={C.hover} fillOpacity={0.5} />
      <line x1={xl} y1={y0} x2={xl} y2={y0 + 30} stroke="currentColor" strokeWidth={1.5} className={C.line} />
      <FieldTag xl={xl} y0={y0} index={index} kind="N" yOff={17} />

      <g onClick={() => onSelectCoupling(f.coupling.id)} className="cursor-pointer">
        <title>{`Trennstelle ${f.coupling.name} — ${f.isClosed ? 'geschlossen' : 'offen'}; im Panel schalten`}</title>
        <SwitchSymbol x={xl} y={y0 + 34} closed={f.isClosed} colorClass={stateClass} />
        <line x1={xl} y1={y0 + 64} x2={xl} y2={y0 + 76} stroke="currentColor" strokeWidth={1.5} className={C.line} />
      </g>
      <T x={xl - 8} y={y0 + 54} anchor="end" size={8.5} mono className={C.faint}>-Q{String(index + 1).padStart(2, '0')}</T>
      <T x={xt} y={y0 + 48} size={10.5} bold>Trennstelle</T>
      <T x={xt} y={y0 + 61} size={9.5} bold className={stateClass}>{f.isClosed ? 'geschlossen' : 'offen'}</T>

      <line
        x1={xl}
        y1={y0 + 76}
        x2={xl}
        y2={y0 + 186}
        stroke="currentColor"
        strokeWidth={2.5}
        strokeDasharray="6 4"
        className={cableColor}
      />
      <polygon points={`${xl - 4},${y0 + 184} ${xl + 4},${y0 + 184} ${xl},${y0 + 192}`} fill="currentColor" className={C.line} />
      <T x={xt} y={y0 + 95} size={10.5} bold title={f.coupling.cableName ?? undefined}>{cut(f.coupling.cableName ?? 'Kuppelkabel', 17)}</T>
      <T x={xt} y={y0 + 108} size={9} className={C.muted}>
        {cut(switchTypeLabel(f.coupling.switchType, 'Kuppelkabel der Nachbarzone'), 22)}
      </T>
      {f.isClosed ? (
        <>
          <SourceDot x={xt + 3} y={y0 + 127} source={flow ? 'calculated' : null} />
          <T x={xt + 9} y={y0 + 130} size={9.5}>{flow?.currentA != null ? `${fmt(flow.currentA, 0)} A` : 'I —'}</T>
          <T x={xt + 9} y={y0 + 144} size={9.5}>P {powerKw == null ? '—' : `${fmt(Math.abs(powerKw), 1)} kW`}</T>
          <T x={xt + 9} y={y0 + 160} size={11} bold className={loading == null ? C.muted : loadingTextClass(loading, cableLevels)}>
            {loading == null ? '—' : `${fmt(loading, 0)} %`}
          </T>
        </>
      ) : (
        <T x={xt + 9} y={y0 + 130} size={9.5} className={C.muted}>stromlos</T>
      )}

      <DestinationBox x0={x0} y={y0 + 200} kind="zone" name={f.otherZoneName} detail={f.coupling.name} />
    </g>
  );
};

const OutletColumn: React.FC<ColumnProps & { field: StationOutletField; cableLevels: LoadLevelThresholds }> = ({ field: f, index, x0, y0, cableLevels }) => {
  const xl = x0 + 40;
  const xt = xl + 13;
  const hasCurrent = f.currentA != null;
  const fuseLoading = hasCurrent && f.fuseSizeA ? (f.currentA! / f.fuseSizeA) * 100 : null;
  const label = f.outlet.name || f.outlet.portName || f.outlet.valuePrefix || 'Outlet';
  return (
    <g className="group">
      <rect x={x0 + 2} y={y0 + 6} width={STATION_COL_W - 4} height={FIELD_H - 8} rx={8} className={C.hover} fillOpacity={0.5} />
      <line x1={xl} y1={y0} x2={xl} y2={y0 + 30} stroke="currentColor" strokeWidth={1.5} strokeDasharray="3 3" className={C.lineSoft} />
      <FieldTag xl={xl} y0={y0} index={index} kind="N" yOff={17} />
      <g>
        <title>Messstelle ohne zugeordnetes Kabel — Zuordnung im Trafo-Panel unter Lemonbeat-Messwerte</title>
        <FuseSymbol x={xl} y={y0 + 36} colorClass={f.fuseSizeA ? loadingColor(fuseLoading, cableLevels) : C.noData} inserted dashed />
      </g>
      <T x={xt} y={y0 + 48} size={10.5} bold>{f.fuseSizeA ? `${fmt(f.fuseSizeA, 0)} A` : 'ohne Angabe'}</T>
      <T x={xt} y={y0 + 61} size={9.5} className={C.muted}>{f.slot != null ? `Slot ${f.slot}` : 'Messstelle'}</T>
      <line x1={xl} y1={y0 + 76} x2={xl} y2={y0 + 186} stroke="currentColor" strokeWidth={2} strokeDasharray="3 4" className={C.lineSoft} />
      <T x={xt} y={y0 + 95} size={10.5} bold title={label}>{cut(label, 17)}</T>
      <T x={xt} y={y0 + 108} size={9} className={C.muted}>kein Kabel zugeordnet</T>
      <SourceDot x={xt + 3} y={y0 + 127} source={hasCurrent ? 'measured' : null} />
      <T x={xt + 9} y={y0 + 130} size={9.5}>{hasCurrent ? `${phases(f.phaseCurrentsA)} A` : 'I —'}</T>
      {fuseLoading != null && (
        <T x={xt + 9} y={y0 + 160} size={11} bold className={loadingTextClass(fuseLoading, cableLevels)}>{fmt(fuseLoading, 0)} % Sich.</T>
      )}
      <DestinationBox x0={x0} y={y0 + 200} kind="none" name={hasCurrent ? 'nicht zugeordnet' : 'Reserve'} dashed />
    </g>
  );
};

// ── Spine ───────────────────────────────────────────────────────────────

const Spine: React.FC<{ spine: StationSpine; cx: number; busbarY: number; trafoLevels: LoadLevelThresholds }> = ({ spine: s, cx, busbarY, trafoLevels }) => {
  const loadingClass = s.loadingPct == null ? C.muted : loadingTextClass(s.loadingPct, trafoLevels);
  const loadingFill = loadingColor(s.loadingPct, trafoLevels);
  const hasU = s.voltageV.some(v => v != null);
  const hasI = s.currentA.some(v => v != null);
  const meterX = cx + 96;
  return (
    <g>
      {/* MV feed */}
      <circle cx={cx} cy={24} r={4.5} fill="currentColor" className={C.line} />
      <line x1={cx} y1={28} x2={cx} y2={68} stroke="currentColor" strokeWidth={1.5} className={C.line} />
      <T x={cx + 12} y={22} size={10.5} bold>MS-Einspeisung</T>
      <T x={cx + 12} y={34} size={9} className={C.muted}>Mittelspannung · {fmt(s.hvKv, s.hvKv % 1 === 0 ? 0 : 1)} kV</T>

      {/* transformer symbol */}
      <circle cx={cx} cy={86} r={17} fill="none" stroke="currentColor" strokeWidth={1.8} className={C.line} />
      <circle cx={cx} cy={110} r={17} fill="none" stroke="currentColor" strokeWidth={1.8} className={C.line} />
      <line x1={cx} y1={127} x2={cx} y2={214} stroke="currentColor" strokeWidth={1.5} className={C.line} />

      {/* nameplate, left */}
      <T x={cx - 30} y={82} anchor="end" size={8.5} mono className={C.faint}>-{s.designator}</T>
      <T x={cx - 30} y={97} anchor="end" size={12} bold>{s.ratingKva != null ? `${fmt(s.ratingKva, 0)} kVA` : 'Nennleistung —'}</T>
      <T x={cx - 30} y={110} anchor="end" size={9.5} className={C.muted}>
        {fmt(s.hvKv, s.hvKv % 1 === 0 ? 0 : 1)}/{fmt(s.lvKv, s.lvKv === 0.4 ? 1 : 2)} kV{s.ratedCurrentA != null ? ` · Iᵣ ${fmt(s.ratedCurrentA, 0)} A` : ''}
      </T>
      {s.nameplate.map((line, i) => (
        <T key={i} x={cx - 30} y={124 + i * 12} anchor="end" size={8.5} className={C.faint} title={line}>{cut(line, 40)}</T>
      ))}

      {/* live figures, right of the transformer */}
      <rect x={cx + 30} y={74} width={64} height={22} rx={11} fill="currentColor" className={loadingFill} opacity={0.18} />
      <T x={cx + 62} y={89} anchor="middle" size={12} bold className={loadingClass} title="Trafo-Auslastung (Lastfluss)">
        {s.loadingPct == null ? '—' : `${fmt(s.loadingPct, 0)} %`}
      </T>
      <T x={cx + 30} y={112} size={9.5}>
        P {s.activePowerKw == null ? '—' : `${fmt(s.activePowerKw, 0)} kW`}
        {' · '}Q {s.reactivePowerKvar == null ? '—' : `${fmt(s.reactivePowerKvar, 0)} kvar`}
      </T>
      <T x={cx + 30} y={125} size={9.5}>
        S {s.apparentPowerKva == null ? '—' : `${fmt(s.apparentPowerKva, 0)} kVA`}
        {s.cosPhi != null ? ` · cos φ ${fmt(s.cosPhi, 2)}` : ''}
        {s.frequencyHz != null ? ` · ${fmt(s.frequencyHz, 2)} Hz` : ''}
      </T>

      {/* current transformer + meters (A A A V) */}
      <circle cx={cx} cy={158} r={8} fill="none" stroke="currentColor" strokeWidth={1.5} className={C.line} />
      <line x1={cx + 8} y1={158} x2={meterX - 14} y2={158} stroke="currentColor" strokeWidth={1} strokeDasharray="3 3" className={C.lineSoft} />
      {['A', 'A', 'A', 'V'].map((m, i) => (
        <g key={i}>
          <circle cx={meterX + i * 30} cy={158} r={11} fill="none" stroke="currentColor" strokeWidth={1.4} className={C.line} />
          <T x={meterX + i * 30} y={162} anchor="middle" size={10} bold>{m}</T>
          <T x={meterX + i * 30} y={140} anchor="middle" size={8} mono className={C.faint}>-P{i + 1}</T>
        </g>
      ))}
      <SourceDot x={meterX - 11} y={184} source={s.currentSource} />
      <T x={meterX - 5} y={187} size={9.5} title="Strom L1 / L2 / L3 an der NS-Seite">
        I {hasI ? `${phases(s.currentA)} A` : '—'}
      </T>
      <SourceDot x={meterX - 11} y={198} source={s.voltageSource} />
      <T x={meterX - 5} y={201} size={9.5} title="Spannung L1 / L2 / L3 gegen N">
        U {hasU ? `${phases(s.voltageV, 1)} V` : '—'}
      </T>
      <T x={meterX - 5} y={214} size={8.5} className={C.muted}>
        {s.measuredAtLabel ? `gemessen ${s.measuredAtLabel}` : 'keine Trafo-Messung'}
        {s.calcAtLabel ? ` · Lastfluss ${s.calcAtLabel}` : ''}
      </T>

      {/* main switch */}
      <SwitchSymbol x={cx} y={214} closed={s.inService} colorClass={s.inService ? C.line : C.open} />
      <line x1={cx} y1={244} x2={cx} y2={busbarY} stroke="currentColor" strokeWidth={1.5} className={C.line} />
      <T x={cx - 30} y={224} anchor="end" size={8.5} mono className={C.faint}>-Q1</T>
      <T x={cx - 30} y={237} anchor="end" size={9.5} bold>NS-Hauptschalter</T>
      <T x={cx - 30} y={249} anchor="end" size={9} className={s.inService ? C.muted : C.open}>
        {s.inService ? 'geschlossen' : 'offen · Trafo außer Betrieb'}
      </T>
    </g>
  );
};

// ── Diagram ─────────────────────────────────────────────────────────────

interface SectionLayout {
  section: StationSection;
  /** First column x and the x after the last column. */
  x1: number;
  x2: number;
  firstFieldIndex: number;
}

export const StationDiagram: React.FC<Props> = ({ spine, sections, switches = [], cableLevels, trafoLevels, fitWidth, ariaLabel, onSelectElement, onSelectCoupling }) => {
  const busbarY = spine ? SPINE_BUSBAR_Y : PLAIN_BUSBAR_Y;
  const totalFields = sections.reduce((n, s) => n + s.fields.length, 0);
  const widths = sections.map(s => Math.max(1, s.fields.length) * STATION_COL_W);
  const fieldsWidth = widths.reduce((a, b) => a + b, 0) + Math.max(0, sections.length - 1) * SECTION_GAP;
  const minWidth = spine ? 820 : 520;
  const width = Math.max(minWidth, PAD_X * 2 + fieldsWidth);
  const height = busbarY + (totalFields > 0 ? FIELD_H : 80) + 12;
  const cx = width / 2;
  const startX = Math.max(PAD_X, (width - fieldsWidth) / 2);

  const layouts: SectionLayout[] = [];
  let x = startX;
  let fieldIndex = 0;
  sections.forEach((section, i) => {
    layouts.push({ section, x1: x, x2: x + widths[i], firstFieldIndex: fieldIndex });
    fieldIndex += section.fields.length;
    x += widths[i] + SECTION_GAP;
  });
  const layoutById = new Map(layouts.map((l, i) => [l.section.id, { ...l, order: i }] as const));

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width={fitWidth ? undefined : width}
      height={fitWidth ? undefined : height}
      style={fitWidth ? { width: '100%', maxWidth: width, height: 'auto' } : undefined}
      preserveAspectRatio="xMidYMin meet"
      className="font-sans text-gray-900 dark:text-white"
      role="img"
      aria-label={ariaLabel}
    >
      {spine && <Spine spine={spine} cx={cx} busbarY={busbarY} trafoLevels={trafoLevels} />}

      {/* busbar sections */}
      {layouts.map(({ section, x1, x2 }) => {
        // The station's single busbar always reaches under the spine.
        const bx1 = spine ? Math.min(x1 - 12, cx - 40) : x1 - 12;
        const bx2 = spine ? Math.max(x2 + 12, cx + 40) : x2 + 12;
        const right = [
          section.maxCurrentA ? `Iₘₐₓ ${fmt(section.maxCurrentA, 0)} A` : null,
          section.voltageV != null ? `U ${fmt(section.voltageV, 1)} V` : null,
          spine ? '3~ 400 V · 50 Hz' : null,
        ].filter(Boolean).join(' · ');
        return (
          <g key={section.id}>
            <line
              x1={bx1}
              y1={busbarY}
              x2={bx2}
              y2={busbarY}
              stroke="currentColor"
              strokeWidth={5}
              strokeLinecap="round"
              strokeDasharray={section.unassigned ? '8 6' : undefined}
              className={section.inService ? C.busbar : C.faint}
            />
            <T x={bx1} y={busbarY - 10} size={8.5} mono className={C.faint}>{section.designator}</T>
            <T x={bx1 + 26} y={busbarY - 10} size={9.5} bold className={section.inService ? C.text : C.open}>
              {cut(section.name, 26)}{section.inService ? '' : ' · außer Betrieb'}
            </T>
            {right && (
              <T x={bx2} y={busbarY - 10} anchor="end" size={9} className={C.muted}>
                {section.voltageV != null && <title>Sammelschienenspannung L-N aus dem Lastfluss</title>}
                {right}
              </T>
            )}
            {section.fields.length === 0 && (
              <T x={(x1 + x2) / 2} y={busbarY + 44} anchor="middle" size={10} className={C.muted}>
                {sections.length === 1 ? 'Keine Abgänge an dieser Sammelschiene bekannt.' : 'keine Abgänge'}
              </T>
            )}
          </g>
        );
      })}

      {/* couplers between busbar sections */}
      {switches.map((sw, i) => {
        const a = layoutById.get(sw.sectionAId);
        const b = layoutById.get(sw.sectionBId);
        if (!a || !b || a.order === b.order) return null;
        const [left, right] = a.order < b.order ? [a, b] : [b, a];
        const adjacent = right.order === left.order + 1;
        const gx1 = left.x2 + 12;
        const gx2 = right.x1 - 12;
        const y = adjacent ? busbarY : busbarY - 46;
        const mid = (gx1 + gx2) / 2;
        const stateClass = sw.isClosed ? C.closed : C.open;
        return (
          <g key={sw.id}>
            <title>{`${sw.name} · ${switchTypeLabel(sw.switchType, 'Kupplung')} · ${sw.isClosed ? 'geschlossen' : 'offen'}`}</title>
            {!adjacent && (
              <>
                <line x1={gx1} y1={busbarY} x2={gx1} y2={y} stroke="currentColor" strokeWidth={1.5} className={C.line} />
                <line x1={gx2} y1={busbarY} x2={gx2} y2={y} stroke="currentColor" strokeWidth={1.5} className={C.line} />
              </>
            )}
            <line x1={gx1} y1={y} x2={mid - 24} y2={y} stroke="currentColor" strokeWidth={1.5} className={C.line} />
            <line x1={mid + 24} y1={y} x2={gx2} y2={y} stroke="currentColor" strokeWidth={1.5} className={C.line} />
            <CouplerSymbol x1={mid - 24} x2={mid + 24} y={y} closed={sw.isClosed} colorClass={stateClass} />
            <T x={mid} y={y - 32} anchor="middle" size={8.5} mono className={C.faint}>-Q{String(i + 1).padStart(2, '0')}</T>
            <T x={mid} y={y - 20} anchor="middle" size={9.5} bold>{cut(sw.name, 14)}</T>
            <T x={mid} y={y + 18} anchor="middle" size={9} bold className={stateClass}>{sw.isClosed ? 'geschlossen' : 'offen'}</T>
            <T x={mid} y={y + 29} anchor="middle" size={8} className={C.muted}>{cut(switchTypeLabel(sw.switchType, 'Kupplung'), 18)}</T>
          </g>
        );
      })}

      {/* fields */}
      {layouts.map(({ section, x1, firstFieldIndex }) =>
        section.fields.map((f, i) => {
          const x0 = x1 + i * STATION_COL_W;
          const index = firstFieldIndex + i;
          if (f.kind === 'cable') {
            return <CableColumn key={f.id} field={f} index={index} x0={x0} y0={busbarY} cableLevels={cableLevels} onSelectElement={onSelectElement} />;
          }
          if (f.kind === 'coupling') {
            return <CouplingColumn key={f.id} field={f} index={index} x0={x0} y0={busbarY} cableLevels={cableLevels} onSelectCoupling={onSelectCoupling} />;
          }
          return <OutletColumn key={f.id} field={f} index={index} x0={x0} y0={busbarY} cableLevels={cableLevels} />;
        }))}
    </svg>
  );
};
